/**
 * Watching a stream over WebRTC with WHEP: one HTTP request carries the browser's offer, the
 * answer comes back, and the video flows straight from the media server.
 *
 * WebRTC rather than HLS because a drone feed is for deciding things now: HLS is several seconds
 * behind, WebRTC well under one. No library -- the protocol is an offer, an answer and a DELETE.
 */

/** Why a feed would not open, in words the TOC can show. */
export class WhepError extends Error {
  readonly status?: number

  constructor(message: string, status?: number) {
    super(message)
    this.status = status
  }
}

export interface WhepAnswer {
  sdp: string
  /** Where to send the DELETE that ends the session, if the server said. */
  session: string | null
}

/** Send the offer and read the answer. Separate from the peer connection so it can be tested. */
export async function exchange(
  url: string,
  offer: string,
  fetcher: typeof fetch = fetch,
): Promise<WhepAnswer> {
  let response: Response
  try {
    response = await fetcher(url, {
      method: 'POST',
      headers: { 'Content-Type': 'application/sdp' },
      body: offer,
    })
  } catch {
    throw new WhepError('Could not reach the video server.')
  }

  if (response.status === 401 || response.status === 403) {
    throw new WhepError('Not allowed to watch this feed. Try opening it again.', response.status)
  }
  if (response.status === 404) {
    throw new WhepError('Nothing is streaming to this feed right now.', 404)
  }
  if (response.status === 502 || response.status === 503) {
    throw new WhepError('The video server is not running on this instance.', response.status)
  }
  if (!response.ok) {
    throw new WhepError(`The video server refused (${response.status}).`, response.status)
  }

  const location = response.headers.get('Location')
  return {
    sdp: await response.text(),
    session: location ? new URL(location, url).toString() : null,
  }
}

/**
 * Open a feed into a video element. Returns a function that closes it.
 *
 * ICE candidates are gathered before the offer is sent rather than trickled: one request instead
 * of a conversation, which suits a media server one hop away and keeps this small. Gathering is
 * capped at two seconds so a slow network interface cannot hold the player at "connecting".
 */
export async function playWhep(
  url: string,
  video: HTMLVideoElement,
  onState?: (state: RTCPeerConnectionState) => void,
): Promise<() => void> {
  const pc = new RTCPeerConnection()
  pc.addTransceiver('video', { direction: 'recvonly' })
  pc.addTransceiver('audio', { direction: 'recvonly' })
  pc.ontrack = (event) => {
    if (video.srcObject !== event.streams[0]) video.srcObject = event.streams[0] ?? null
  }
  pc.onconnectionstatechange = () => onState?.(pc.connectionState)

  try {
    await pc.setLocalDescription(await pc.createOffer())
    await gathered(pc, 2000)
    const answer = await exchange(url, pc.localDescription?.sdp ?? '')
    await pc.setRemoteDescription({ type: 'answer', sdp: answer.sdp })

    return () => {
      pc.close()
      video.srcObject = null
      if (answer.session) void fetch(answer.session, { method: 'DELETE' }).catch(() => {})
    }
  } catch (error) {
    pc.close()
    throw error
  }
}

function gathered(pc: RTCPeerConnection, timeoutMs: number): Promise<void> {
  if (pc.iceGatheringState === 'complete') return Promise.resolve()
  return new Promise((resolve) => {
    const done = () => {
      pc.removeEventListener('icegatheringstatechange', check)
      clearTimeout(timer)
      resolve()
    }
    const check = () => {
      if (pc.iceGatheringState === 'complete') done()
    }
    const timer = setTimeout(done, timeoutMs)
    pc.addEventListener('icegatheringstatechange', check)
  })
}

/** The address of a feed's player, from the relative one the server hands out. */
export function absoluteWhepUrl(relative: string, server: string): string {
  return new URL(relative, server || window.location.origin).toString()
}

/** The full address to paste into DJI Fly: the RTMP address with the key on the end. */
export function djiFlyAddress(publishUrl: string, streamKey: string): string {
  return `${publishUrl}?key=${encodeURIComponent(streamKey)}`
}
